<template>
  <v-dialog v-model="internalShow" max-width="850" persistent scrollable>
    <v-card class="rounded-lg">
      <v-card-title class="pa-4 primary white--text d-flex justify-space-between align-center">
        <div class="d-flex align-center">
          <v-icon color="white" class="me-2" size="24">mdi-history</v-icon>
          <div>
            <div class="text-h6 font-weight-bold white--text">
              سجل حركة الرحلة #{{ plannedTripId }}
            </div>
            <div class="text-caption white--text opacity-90">
              عرض تتابعي لنقاط GPS المعتمدة برمجياً
            </div>
          </div>
        </div>
        <v-btn icon dark @click="closeModal">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-4" style="max-height: 75vh;">
        <!-- Loading State -->
        <div v-if="loading" class="text-center py-10">
          <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
          <div class="mt-3 text-body-2 grey--text text--darken-1 font-weight-medium">
            جاري تحميل سجل التتبع المكاني من قاعدة البيانات...
          </div>
        </div>

        <div v-else-if="error" class="pa-6 text-center">
          <v-alert type="error" text class="mb-4">{{ error }}</v-alert>
          <v-btn color="primary" @click="fetchLocationLogs">إعادة المحاولة</v-btn>
        </div>

        <div v-else-if="logs.length === 0" class="text-center py-10 grey--text">
          <v-icon size="64" color="grey lighten-1">mdi-map-marker-off</v-icon>
          <div class="mt-2 text-h6 font-weight-bold">لا يوجد سجل حركة مسجل لهذه الرحلة</div>
          <div class="text-caption">لم يتم تسجيل نقاط GPS موقعية أثناء هذه الرحلة حتى الآن.</div>
        </div>

        <div v-else>
          <!-- Summary Metrics -->
          <v-row dense class="mb-4">
            <v-col cols="4">
              <v-card outlined class="pa-2 text-center rounded-lg grey lighten-5">
                <div class="text-caption font-weight-bold grey--text text--darken-2">إجمالي النقاط المسجلة</div>
                <div class="text-h6 font-weight-black primary--text mt-1">{{ logs.length }}</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined class="pa-2 text-center rounded-lg grey lighten-5">
                <div class="text-caption font-weight-bold grey--text text--darken-2">أول نقطة موقعية</div>
                <div class="text-subtitle-2 font-weight-bold mt-1 text-truncate">{{ logs[0].recorded_at }}</div>
              </v-card>
            </v-col>
            <v-col cols="4">
              <v-card outlined class="pa-2 text-center rounded-lg grey lighten-5">
                <div class="text-caption font-weight-bold grey--text text--darken-2">آخر نقطة موقعية</div>
                <div class="text-subtitle-2 font-weight-bold mt-1 text-truncate">{{ logs[logs.length - 1].recorded_at }}</div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Timeline of Location Logs -->
          <v-card outlined class="rounded-lg pa-3">
            <div class="text-subtitle-2 font-weight-bold mb-3 primary--text">
              <v-icon size="18" color="primary" class="me-1">mdi-timeline-text</v-icon>
              التسلسل الزمني لنقاط الموقع
            </div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-right">#</th>
                    <th class="text-right">الوقت المسجل</th>
                    <th class="text-right">الإحداثيات (Lat, Lng)</th>
                    <th class="text-right">السرعة</th>
                    <th class="text-right">الدقة</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(log, idx) in logs" :key="log.id || idx">
                    <td class="font-weight-bold text-caption">{{ idx + 1 }}</td>
                    <td class="text-caption font-weight-medium">{{ log.recorded_at }}</td>
                    <td class="text-caption font-weight-bold" dir="ltr" style="text-align: right;">
                      {{ log.latitude }}, {{ log.longitude }}
                    </td>
                    <td class="text-caption">
                      {{ log.speed !== null && log.speed !== undefined ? log.speed + ' كم/س' : '—' }}
                    </td>
                    <td class="text-caption">
                      {{ log.accuracy !== null && log.accuracy !== undefined ? log.accuracy + ' م' : '—' }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </div>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3 bg-light">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text class="font-weight-bold" @click="closeModal">إغلاق</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "GpsHistoryModal",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    plannedTripId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      logs: [],
    };
  },
  computed: {
    internalShow: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
  watch: {
    value(newVal) {
      if (newVal && this.plannedTripId) {
        this.fetchLocationLogs();
      }
    },
  },
  methods: {
    closeModal() {
      this.internalShow = false;
    },
    fetchLocationLogs() {
      if (!this.plannedTripId) return;
      this.loading = true;
      this.error = null;
      this.logs = [];

      axios
        .get(`/planned-trips/${this.plannedTripId}/location-logs`)
        .then((res) => {
          if (res.data && res.data.success) {
            this.logs = res.data.logs || [];
          }
        })
        .catch((err) => {
          console.error("Error fetching trip location logs:", err);
          this.error = "تعذر تحميل سجل حركة الرحلة. يرجى المحاولة لاحقاً.";
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
